import React, { useState } from "react";
import { Scale, Loader2, Plus, X, Search } from "lucide-react";
import { toast } from "sonner";
import { renderMarkdown } from "../lib/markdown";
import { api, triangulateChat } from "../lib/api";
import SavePublishBar from "./SavePublishBar";
import InlineToolChat from "./InlineToolChat";

/**
 * Source Triangulator.
 * User drops 2–6 article URLs + an optional focus question. AI fetches each, then returns:
 *   - AGREEMENT (facts all sources share)
 *   - DISAGREEMENT (named conflicts between sources)
 *   - CREDIBILITY RANKING
 *   - WHAT'S MISSING
 *   - VERDICT
 * Embedded on the Connect-the-Dots page so power users can stress-test claims.
 */
export default function SourceTriangulator() {
  const [urls, setUrls] = useState(["", ""]);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const setUrl = (i, v) => setUrls((arr) => arr.map((u, idx) => (idx === i ? v : u)));
  const addRow = () => urls.length < 6 && setUrls((arr) => [...arr, ""]);
  const removeRow = (i) => urls.length > 2 && setUrls((arr) => arr.filter((_, idx) => idx !== i));

  const reset = () => {
    setUrls(["", ""]);
    setQuestion("");
    setResult(null);
  };

  const onSubmit = async (e) => {
    e?.preventDefault();
    const clean = urls.map((u) => u.trim()).filter(Boolean);
    if (clean.length < 2) { toast.error("Need at least 2 URLs"); return; }
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/ai/triangulate", { urls: clean, question: question.trim() || null });
      setResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Triangulation failed");
    } finally { setLoading(false); }
  };

  return (
    <section className="mt-12 border border-[#60a5fa]/30 bg-[#0a0a0a]" data-testid="source-triangulator">
      <div className="px-5 py-3 border-b border-[#60a5fa]/30 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Scale size={12} className="text-[#60a5fa]" />
          <span className="font-mono text-[10px] tracking-[0.3em] text-[#60a5fa]">// SOURCE TRIANGULATOR</span>
        </div>
        <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500">
          POWER MODE · STRESS-TEST 2–6 SOURCES
        </span>
      </div>

      <div className="px-5 py-4">
        <p className="text-zinc-400 text-sm mb-4 max-w-2xl leading-relaxed">
          Paste 2–6 URLs. AI fetches each, then shows you what they <span className="text-[#00ff88]">AGREE</span> on,
          what they <span className="text-[#ff3b30]">DISAGREE</span> on, ranks them by credibility,
          and flags what&apos;s MISSING.
        </p>

        <form onSubmit={onSubmit}>
          {urls.map((u, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <span className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 w-6 pt-2.5">{(i + 1).toString().padStart(2, "0")}</span>
              <input type="url" value={u} onChange={(e) => setUrl(i, e.target.value)}
                data-testid={`triangulate-url-${i}`} placeholder="https://…"
                className="flex-1 bg-[#050505] border border-white/15 focus:border-[#60a5fa] outline-none px-3 py-2 font-mono text-sm text-white transition-colors" />
              {urls.length > 2 && (
                <button type="button" onClick={() => removeRow(i)} className="px-2 text-zinc-500 hover:text-[#ff3b30]">
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
          {urls.length < 6 && (
            <button type="button" onClick={addRow} data-testid="triangulate-add-row"
              className="font-mono text-[10px] tracking-[0.2em] text-[#60a5fa] hover:text-white inline-flex items-center gap-1 mt-1 mb-3">
              <Plus size={11} /> ADD SOURCE
            </button>
          )}
          <input value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={500}
            data-testid="triangulate-question" placeholder="Focus question (optional) — e.g. 'Did the Wuhan Institute leak the virus?'"
            className="w-full bg-[#050505] border border-white/15 focus:border-[#60a5fa] outline-none px-3 py-2 font-mono text-sm text-white transition-colors mt-3 mb-3" />
          <button type="submit" disabled={loading} data-testid="triangulate-submit"
            className="px-5 py-2 font-mono text-[11px] uppercase tracking-[0.2em] bg-[#60a5fa] text-[#050505] hover:bg-white transition-colors disabled:opacity-50 inline-flex items-center gap-2">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
            {loading ? "Triangulating…" : "Triangulate"}
          </button>
          {(result || urls.some(Boolean) || question) && (
            <button
              type="button"
              onClick={reset}
              disabled={loading}
              data-testid="triangulate-reset"
              title="Clear inputs and result"
              className="ml-2 inline-flex items-center gap-1.5 border border-white/15 text-zinc-400 hover:text-white hover:border-white/40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] disabled:opacity-50"
            >
              <X size={11} /> Reset
            </button>
          )}
        </form>
      </div>

      {result && (
        <div data-testid="triangulate-result" className="border border-white/10 bg-[#0a0a0a] p-5">
          <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-3">
            // ANALYZED {result.sources.filter((s) => s.ok).length} OF {result.sources.length} SOURCES
          </div>
          <div className="markdown text-zinc-200 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: renderMarkdown(result.result) }} />
          {result.sources.some((s) => !s.ok) && (
            <div className="mt-4 text-xs text-[#ff9500] font-mono tracking-[0.15em]">
              // SKIPPED: {result.sources.filter((s) => !s.ok).map((s) => s.url).join(", ")}
            </div>
          )}
          <SavePublishBar
            kind="source_triangulator"
            topic={question.trim() || result.sources?.[0]?.url || "Triangulation"}
            getPayload={() => ({ ...result, urls: urls.filter(Boolean), question })}
            testId="triangulate-save"
          />
          <InlineToolChat
            context={result.result}
            sendChat={triangulateChat}
            accent="#60a5fa"
            title="Ask follow-ups about these sources"
            placeholder="e.g. Which source is most likely correct on the date?"
            testIdRoot="triangulate-chat"
          />
        </div>
      )}
    </section>
  );
}
